import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import './NotificationBell.css';

const formatTimeAgo = (dateInput) => {
  if (!dateInput) return 'Just now';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'Just now';

  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (notif) => {
    markAsRead(notif.id || notif._id);
    setIsOpen(false);

    const type = (notif.type || notif.data?.type || '').toLowerCase();
    const orderId = notif.data?.orderId || notif.orderId;

    if (type.includes('refund') || type.includes('wallet')) {
      navigate('/refunds');
    } else if (orderId) {
      navigate(`/order-details/${orderId}`);
    } else if (type.includes('order')) {
      navigate('/order-tracking');
    }
  };

  return (
    <div className="notif-bell-container" ref={dropdownRef}>
      <button
        className="notif-bell-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
        aria-label="Notifications"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {unreadCount > 0 && (
          <span className="notif-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <div className="notif-title-row">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <span className="notif-count-pill">{unreadCount} unread</span>
              )}
            </div>
            {notifications.length > 0 && unreadCount > 0 && (
              <button className="notif-mark-all-btn" onClick={markAllAsRead}>
                Mark all as read
              </button>
            )}
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty-state">
                <div className="notif-empty-icon">🔕</div>
                <p className="notif-empty-text">No notifications yet</p>
                <span className="notif-empty-sub">We'll alert you when your order or refund status updates!</span>
              </div>
            ) : (
              notifications.map((notif, idx) => {
                const notifId = notif.id || notif._id || idx;
                const isUnread = !notif.read;
                const type = (notif.type || '').toLowerCase();

                let icon = '🔔';
                if (type.includes('refund') || type.includes('wallet')) icon = '💰';
                else if (type.includes('order')) icon = '🛍️';

                return (
                  <div
                    key={notifId}
                    className={`notif-item ${isUnread ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <div className="notif-item-icon">{icon}</div>
                    <div className="notif-item-content">
                      <div className="notif-item-title">{notif.title}</div>
                      <div className="notif-item-msg">{notif.message}</div>
                      <div className="notif-item-time">{formatTimeAgo(notif.timestamp || notif.createdAt)}</div>
                    </div>
                    {isUnread && <div className="notif-unread-dot" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
